import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEditableDocument } from "../../contexts/EditableDocumentContext";
import { useProposal } from "../../contexts/ProposalContext";
import { apiClient } from "../../services/apiClient";

// Components
import Layout from "../layout/Layout";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";
import DocumentSidebar from "./DocumentSidebar";
import SectionChatWithArtifact from "./SectionChatWithArtifact";
import SectionTipTapEditor from "./SectionTipTapEditor";
import SectionComparisonModal from "./SectionComparisonModal";
import AnalyzeMissingSectionsModal from "./AnalyzeMissingSectionsModal";
import AddCustomSectionModal from "./AddCustomSectionModal";

const DocumentBuilderAdvanced = () => {
  const { proposalId, documentId } = useParams();
  const navigate = useNavigate();

  const {
    documents,
    currentDocument,
    loading,
    error,
    fetchDocuments,
    fetchDocument,
    updateSection,
    clearError,
  } = useEditableDocument();

  const { currentProposal, fetchProposal } = useProposal();

  // Local state
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionContent, setSectionContent] = useState("");
  const [previewContent, setPreviewContent] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [proposalDocuments, setProposalDocuments] = useState([]);
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  // Load proposal and document data
  useEffect(() => {
    if (proposalId) {
      fetchProposal(parseInt(proposalId));
      fetchDocuments(parseInt(proposalId));
      fetchProposalDocuments(parseInt(proposalId));
    }
  }, [proposalId, fetchProposal, fetchDocuments]);

  // Fetch uploaded proposal documents (RFP, etc.)
  const fetchProposalDocuments = async (propId) => {
    try {
      const response = await apiClient.getProposalDocuments(propId);
      if (response.success) {
        setProposalDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error("Error fetching proposal documents:", error);
    }
  };

  // Load specific document
  useEffect(() => {
    if (documentId) {
      fetchDocument(parseInt(documentId));
    }
  }, [documentId, fetchDocument]);

  // Auto-select first section when document loads
  useEffect(() => {
    if (currentDocument?.sections?.length > 0 && !selectedSection) {
      const firstSection = currentDocument.sections[0];
      setSelectedSection(firstSection);
      setSectionContent(firstSection.content || "");
    }
  }, [currentDocument, selectedSection]);

  // Track content changes
  useEffect(() => {
    if (selectedSection && sectionContent !== (selectedSection.content || "")) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [sectionContent, selectedSection]);

  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges || !selectedSection || !sectionContent.trim()) return;

    const autoSaveTimer = setTimeout(async () => {
      try {
        setAutoSaveStatus("Auto-saving...");
        await handleAutoSave();
        setAutoSaveStatus("Auto-saved");
        setTimeout(() => setAutoSaveStatus(""), 2000);
      } catch (error) {
        setAutoSaveStatus("Auto-save failed");
        setTimeout(() => setAutoSaveStatus(""), 3000);
        console.error("Auto-save failed:", error);
      }
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, sectionContent, selectedSection]);

  const handleAutoSave = async () => {
    if (!selectedSection || !sectionContent.trim()) return;

    await updateSection(selectedSection.section_id, {
      content: sectionContent
    });

    setHasUnsavedChanges(false);

    // Update the section in state
    if (currentDocument) {
      const updatedSections = currentDocument.sections.map(section =>
        section.section_id === selectedSection.section_id
          ? { ...section, content: sectionContent }
          : section
      );

      // This would need to be handled by the context, but for now just update local state
      setSelectedSection({ ...selectedSection, content: sectionContent });
    }
  };

  const handleSectionSelect = async (section) => {
    // Save current section if there are unsaved changes
    if (hasUnsavedChanges && selectedSection) {
      await handleAutoSave();
    }

    setSelectedSection(section);
    setSectionContent(section.content || "");
    setPreviewContent(null);
    setShowComparison(false);
    setCurrentChatSession(null);
  };

  const handleArtifactUpdate = async (content, action, messageId = null) => {
    if (action === "create" || action === "update") {
      setPreviewContent({
        content,
        messageId,
        action
      });
      setShowComparison(true);
    }
  };

  const handleAcceptChanges = async () => {
    if (!previewContent || !selectedSection) return;

    try {
      // Update section content
      setSectionContent(previewContent.content);

      // Save to backend
      await updateSection(selectedSection.section_id, {
        content: previewContent.content
      });

      // Accept the content via API for audit trail
      await apiClient.acceptSectionContent(
        selectedSection.section_id,
        previewContent.content,
        previewContent.messageId,
        `AI-generated content accepted`
      );

      setShowComparison(false);
      setPreviewContent(null);
      setHasUnsavedChanges(false);

      // Update selected section
      setSelectedSection({
        ...selectedSection,
        content: previewContent.content
      });

    } catch (error) {
      console.error("Failed to accept changes:", error);
      // Handle error appropriately
    }
  };

  const handleRejectChanges = () => {
    setPreviewContent(null);
    setShowComparison(false);
  };

  const handleManualSave = async () => {
    try {
      setAutoSaveStatus("Saving...");
      await handleAutoSave();
      setAutoSaveStatus("Saved");
      setTimeout(() => setAutoSaveStatus(""), 2000);
    } catch (error) {
      setAutoSaveStatus("Save failed");
      setTimeout(() => setAutoSaveStatus(""), 3000);
      console.error("Manual save failed:", error);
    }
  };

  const handleBackToDocuments = () => {
    navigate(`/workspace/${proposalId}/documents`);
  };

  const handleSectionsGenerated = () => {
    // Refresh document to show new sections
    fetchDocument(parseInt(documentId));
  };

  const handleDeleteSection = (section) => {
    setSectionToDelete(section);
    setShowDeleteSectionModal(true);
  };

  const handleConfirmDeleteSection = async () => {
    if (!sectionToDelete) return;

    try {
      await apiClient.deleteDocumentSection(sectionToDelete.section_id);

      // Refresh document to update section list
      await fetchDocument(parseInt(documentId));

      // If we deleted the currently selected section, select another
      if (selectedSection?.section_id === sectionToDelete.section_id) {
        // Select first available section or null
        const remainingSections = currentDocument.sections.filter(
          s => s.section_id !== sectionToDelete.section_id
        );
        if (remainingSections.length > 0) {
          handleSectionSelect(remainingSections[0]);
        } else {
          setSelectedSection(null);
          setSectionContent("");
        }
      }

      setShowDeleteSectionModal(false);
      setSectionToDelete(null);
    } catch (error) {
      console.error("Failed to delete section:", error);
      alert("Failed to delete section. Please try again.");
    }
  };

  const handleCancelDeleteSection = () => {
    setShowDeleteSectionModal(false);
    setSectionToDelete(null);
  };

  if (!proposalId || !documentId) {
    return (
      <Layout title="Document Builder">
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="file-alt"
            className="text-4xl text-slate-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Invalid Document
          </h3>
          <p className="text-slate-600 mb-6">
            The document URL is invalid or missing parameters.
          </p>
          <button
            onClick={() => navigate("/proposals")}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Proposals
          </button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Document Builder">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading document..." />
        </div>
      </Layout>
    );
  }

  if (!currentDocument) {
    return (
      <Layout title="Document Builder">
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            className="text-4xl text-amber-500 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Document Not Found
          </h3>
          <p className="text-slate-600 mb-6">
            The requested document could not be found or you don't have access to it.
          </p>
          <button
            onClick={() => navigate(`/proposals/${proposalId}/documents`)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Documents
          </button>
        </div>
      </Layout>
    );
  }

  const sections = currentDocument.sections || [];

  return (
    <Layout
      title={`${currentDocument.document_name} - Advanced Editor`}
      showBreadcrumb={true}
      breadcrumbItems={[
        { label: "Proposals", href: "/proposals" },
        {
          label: currentProposal?.proposal_name || "Proposal",
          href: `/proposals/${proposalId}`
        },
        {
          label: "Documents",
          href: `/proposals/${proposalId}/documents`
        },
        {
          label: currentDocument.document_name,
          href: `/proposals/${proposalId}/documents/${documentId}`,
          current: true
        }
      ]}
    >
      {/* Error Display */}
      {error && <ErrorMessage message={error} onRetry={clearError} />}

      {/* Three Panel Layout */}
      <div className="h-[calc(100vh-100px)] flex bg-gray-50">
        {/* Left Panel - Document Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex-shrink-0">
          <DocumentSidebar
            document={currentDocument}
            sections={sections}
            selectedSection={selectedSection}
            onSectionSelect={handleSectionSelect}
            proposal={currentProposal}
            hasUnsavedChanges={hasUnsavedChanges}
            proposalDocuments={proposalDocuments}
            onBackToDocuments={handleBackToDocuments}
            onAnalyzeSections={() => setShowAnalyzeModal(true)}
            onAddSection={() => setShowAddSectionModal(true)}
            onDeleteSection={handleDeleteSection}
          />
        </div>

        {/* Middle Panel - Chat Interface */}
        <div className="flex-1 max-w-2xl flex flex-col border-r border-gray-200">
          {selectedSection ? (
            <SectionChatWithArtifact
              section={selectedSection}
              documentId={parseInt(documentId)}
              currentContent={sectionContent}
              onArtifactUpdate={handleArtifactUpdate}
              onSessionChange={setCurrentChatSession}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <FontAwesomeIcon icon="comments" className="text-4xl mb-4" />
                <p>Select a section to start chatting with AI</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - TipTap Editor */}
        <div className="flex-1 flex flex-col">
          {selectedSection ? (
            <SectionTipTapEditor
              section={selectedSection}
              content={sectionContent}
              onChange={setSectionContent}
              onSave={handleManualSave}
              autoSaveStatus={autoSaveStatus}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-white">
              <div className="text-center text-gray-500">
                <FontAwesomeIcon icon="edit" className="text-4xl mb-4" />
                <p>Select a section to start editing</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Modal */}
      {showComparison && previewContent && (
        <SectionComparisonModal
          isOpen={showComparison}
          currentContent={sectionContent}
          proposedContent={previewContent.content}
          sectionTitle={selectedSection?.section_title}
          onAccept={handleAcceptChanges}
          onReject={handleRejectChanges}
          action={previewContent.action}
        />
      )}

      {/* Analyze Missing Sections Modal */}
      <AnalyzeMissingSectionsModal
        isOpen={showAnalyzeModal}
        onClose={() => setShowAnalyzeModal(false)}
        documentId={parseInt(documentId)}
        onSectionsGenerated={handleSectionsGenerated}
      />

      {/* Add Custom Section Modal */}
      <AddCustomSectionModal
        isOpen={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        documentId={parseInt(documentId)}
        sections={sections}
        onSectionGenerated={handleSectionsGenerated}
      />

      {/* Delete Section Confirmation Modal */}
      {showDeleteSectionModal && sectionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    className="text-red-600 text-xl"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Delete Section
                </h3>
              </div>
              <p className="text-slate-600 mb-2">
                Are you sure you want to delete the section{" "}
                <span className="font-medium">
                  "{sectionToDelete.section_title}"
                </span>
                ?
              </p>
              <p className="text-sm text-slate-500 mb-6">
                This action cannot be undone. The section content ({sectionToDelete.word_count || 0} words) will be permanently deleted.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDeleteSection}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteSection}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DocumentBuilderAdvanced;